import i18n from '@/utils/i18n';
import MergeIcon from '@assets/icons/merged.svg';
import RemoveIcon from '@assets/icons/trash.svg';
import SingleEmployeeIcon from '@assets/icons/single-employee.svg';
import { createStyles } from '@/contexts/theme-context';
import { type FC } from 'react';
import { FONTS, type ThemedSVGProps } from '@/styles';
import { getMergeDisabledMessage } from '@/screens/order-inbounds/utils';
import {
  type StyleProp,
  Text,
  type TextStyle,
  TouchableOpacity,
  type TouchableOpacityProps,
  type ViewStyle,
} from 'react-native';
import { type SvgProps } from 'react-native-svg';
import { Type } from '@/components/toast';
import { useToasts } from '@/contexts/toast-context';
import type { OrderInboundAssignedEmployee } from '@/screens/order-inbounds/types';

interface OrderInboundsOrderListItemSwipeableActionProps
  extends TouchableOpacityProps {
  containerStyle?: StyleProp<ViewStyle>;
  Icon: FC<SvgProps>;
  iconProps?: ThemedSVGProps;
  text: string;
  textStyle?: StyleProp<TextStyle>;
}

export const OrderInboundsOrderListItemSwipeableAction: FC<
  OrderInboundsOrderListItemSwipeableActionProps
> = ({ containerStyle, Icon, iconProps, text, textStyle, ...props }) => {
  const [styles] = useStyles();

  return (
    <TouchableOpacity
      hitSlop={{ bottom: 0, left: 0, right: 0, top: 0 }}
      style={[styles.action, styles.actionMobile, containerStyle]}
      {...props}
    >
      <Icon height={24} width={24} {...(iconProps ? iconProps : {})} />

      <Text style={[FONTS.primarySemiBold, textStyle]}>{text}</Text>
    </TouchableOpacity>
  );
};

interface OrderInboundsOrderListItemSwipeableActionsProps {
  assignedEmployee?: OrderInboundAssignedEmployee;
  canMerge: boolean;
  onAssignEmployee: () => void;
  onMerge: () => void;
  onRemove: () => void;
  onUnassignEmployee: () => void;
  restaurantName?: string | null;
}

export const OrderInboundsOrderListItemSwipeableActions: FC<
  OrderInboundsOrderListItemSwipeableActionsProps
> = ({
  assignedEmployee,
  canMerge,
  onAssignEmployee,
  onMerge,
  onRemove,
  onUnassignEmployee,
  restaurantName,
}) => {
  const [styles, theme] = useStyles();
  const { showToast } = useToasts();

  const mergeDisabledMessage = getMergeDisabledMessage(restaurantName);

  const onMergePress = () => {
    if (!canMerge) {
      showToast({
        content: mergeDisabledMessage,
        type: Type.DEFAULT,
      });
      return;
    }
    onMerge();
  };

  return (
    <>
      <OrderInboundsOrderListItemSwipeableAction
        containerStyle={styles.removeActionContainer}
        Icon={RemoveIcon}
        iconProps={{ fill: theme.colors.white }}
        onPress={onRemove}
        testID="remove-order-button"
        text={i18n.t('remove')}
        textStyle={styles.removeIconText}
      />

      <OrderInboundsOrderListItemSwipeableAction
        containerStyle={[
          styles.mergeActionContainer,
          !canMerge ? styles.mergeActionContainerDisabled : null,
        ]}
        Icon={MergeIcon}
        iconProps={{
          fill: canMerge
            ? theme.sk.color.icon.primary
            : theme.sk.color.icon.ondark,
        }}
        onPress={onMergePress}
        testID="order-inbounds-merge-order-swipe-icon"
        text={i18n.t('merge')}
        textStyle={[
          styles.mergeIconText,
          !canMerge ? styles.mergeIconTextDisabled : null,
        ]}
      />

      <TouchableOpacity
        hitSlop={{ bottom: 0, left: 0, right: 0, top: 0 }}
        onPress={assignedEmployee ? onUnassignEmployee : onAssignEmployee}
        style={[
          styles.action,
          styles.actionMobile,
          styles.assignActionContainer,
        ]}
        testID="assign-order-button"
      >
        <SingleEmployeeIcon
          height={24}
          stroke={theme.sk.color.icon.ondark}
          width={24}
        />

        <Text style={[FONTS.primarySemiBold, styles.assignIconText]}>
          {assignedEmployee
            ? i18n.t('employee_assignment.unassign')
            : i18n.t('employee_assignment.assign')}
        </Text>
      </TouchableOpacity>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  action: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionMobile: {
    gap: 4,
    margin: 0,
  },
  assignActionContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.icon.blue,
    justifyContent: 'center',
    width: 76,
  },
  assignIconText: {
    color: theme.sk.color.text.ondark,
    fontSize: 11,
    lineHeight: 12,
  },
  mergeActionContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.icon.warning.secondary,
    justifyContent: 'center',
    width: 76,
  },
  mergeActionContainerDisabled: {
    backgroundColor: theme.sk.color.bg.button.primary.disabled,
  },
  mergeIconText: {
    color: theme.colors.secondary,
    fontSize: 11,
    lineHeight: 12,
  },
  mergeIconTextDisabled: {
    color: theme.sk.color.text.ondark,
  },
  removeActionContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.negative,
    justifyContent: 'center',
    width: 76,
  },
  removeIconText: {
    color: theme.colors.white,
    fontSize: 11,
    lineHeight: 12,
  },
}));
